import React, {useState, useMemo, useEffect} from 'react';
import {AvailableSerial} from '../models/distribution.model';
import {Search, Hash} from 'lucide-react';

interface SimpleSerialSearchProps {
  serials: AvailableSerial[];
  selectedSerials: string[];
  onSerialToggle: (serialNumber: string) => void;
  onSelectAll?: () => void;
  onClearSelection?: () => void;
  isLoading?: boolean;
}

export const SimpleSerialSearch: React.FC<SimpleSerialSearchProps> = ({
  serials,
  selectedSerials,
  onSerialToggle,
  onSelectAll,
  onClearSelection,
  isLoading = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Safety check to ensure selectedSerials is an array
  const safeSelectedSerials = Array.isArray(selectedSerials) ? selectedSerials : [];

  // Force re-render when selectedSerials changes
  const [forceUpdate, setForceUpdate] = useState(0);

  useEffect(() => {
    setForceUpdate(prev => prev + 1);
  }, [selectedSerials]);

  // Filter serials based on search
  const filteredSerials = useMemo(() => {
    if (!searchTerm.trim()) {return serials;}

    return serials.filter(serial =>
      serial.serialNumber.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [serials, searchTerm]);

  return (
    <div className="space-y-4">
      {/* Simple Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="البحث في السيريلات..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-brand-500"
        />
      </div>

      {/* Simple Stats */}
      <div className="flex justify-between items-center text-sm text-gray-600">
        <span>إجمالي السيريلات: {serials.length}</span>
        <span>المحددة: {safeSelectedSerials.length}</span>
        {searchTerm && <span>نتائج البحث: {filteredSerials.length}</span>}
      </div>

      {/* Simple Actions */}
      <div className="flex gap-2">
        {onSelectAll && (
          <button
            type="button"
            onClick={onSelectAll}
            disabled={serials.length === 0}
            className="px-3 py-1 text-sm bg-brand-600 text-white rounded hover:bg-brand-700 disabled:opacity-50"
          >
            تحديد الكل
          </button>
        )}
        {onClearSelection && (
          <button
            type="button"
            onClick={onClearSelection}
            disabled={safeSelectedSerials.length === 0}
            className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50"
          >
            إلغاء تحديد الكل
          </button>
        )}
      </div>

      {/* Serials Grid */}
      <div className="border border-gray-300 rounded-lg p-4 max-h-80 overflow-y-auto">
        {isLoading ? (
          <div className="text-center text-gray-500 py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto mb-2"></div>
            جاري تحميل السيريلات...
          </div>
        ) : filteredSerials.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {filteredSerials.map((serial) => (
                <button
                  key={`${serial.id}-${forceUpdate}`}
                  type="button"
                  onClick={() => onSerialToggle(serial.serialNumber)}
                  className={`p-3 rounded-lg border-2 transition-colors ${
                    safeSelectedSerials.includes(serial.serialNumber)
                      ? 'bg-brand-100 border-brand-500 text-brand-800'
                      : 'bg-white border-gray-300 text-gray-700 hover:border-brand-300 hover:bg-brand-50'
                  }`}
                >
                <div className="font-mono font-bold text-center text-lg">
                  {serial.serialNumber}
                </div>
                {serial.locationName && (
                  <div className="text-xs text-center text-gray-600 mt-1">
                    {serial.locationName}
                  </div>
                )}
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-500 py-8">
            {searchTerm ? (
              <div>
                <Search className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>لا توجد نتائج للبحث</p>
              </div>
            ) : (
              <div>
                <Hash className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>لا توجد سيريلات متاحة</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Selected Summary */}
      {safeSelectedSerials.length > 0 && (
        <div className="bg-brand-50 p-3 rounded-lg border border-brand-200">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-brand-900">السيريلات المحددة ({safeSelectedSerials.length})</span>
            {onClearSelection && (
              <button
                type="button"
                onClick={onClearSelection}
                className="text-sm text-brand-600 hover:text-brand-800"
              >
                إلغاء تحديد الكل
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1">
            {safeSelectedSerials.slice(0, 5).map(serial => (
              <span
                key={serial}
                className="px-2 py-1 bg-brand-100 text-brand-800 rounded text-sm font-mono"
              >
                {serial}
              </span>
            ))}
            {safeSelectedSerials.length > 5 && (
              <span className="px-2 py-1 bg-brand-100 text-brand-800 rounded text-sm">
                +{safeSelectedSerials.length - 5} أكثر
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
